import {
	ReactElement,
	useCallback,
	useMemo
} from "react";
import { useSelectFilesDialog } from "../Documents/Dialogs/SelectFilesDialog";
import { documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import {
	FileSelection,
	ItemPickerLocation,
	ItemPickerMode
} from "../Documents/ItemPicker/ItemPicker";
import { FileTypeFilterKind } from "client/Api/Documents/SharePoint/common";
import { getDownloadFileUrl } from "client_legacy/Documents/SharePoint/UrlsProviders/documentsItemUrlsProviders";
import { getFileDownloadUrl } from "../Documents/WebDav/actions";
import { never } from "client_legacy/ndaw/errors";
import { HttpClient } from "client/Api/httpClient";
import { DocumentLocationViewModel } from "client/Api/Hub/hubSettings";
import { convertToItemPickerLocation } from "./Management/HubManagementDialog/Content/Settings/DocumentLocationSelector";
import { useUnmountedCancellation } from "client/Hooks";

export type SelectImageFromDocumentPickerOptions = {
	supportedImageExtensions: readonly string[];
	defaultImageLocation: DocumentLocationViewModel | null;
	onIsBusyChange: (isLoading: boolean) => void;
	isMultiple?: boolean;
};

export type SelectImageFromDocumentPickerResult = {
	dialogElement: ReactElement | null;
	onSelectImage: () => Promise<TryResult<readonly PendingImageDownload[]>>;
};

export type PendingImageDownload = {
	file: Promise<File>;
	cancel: () => void;
};

export function useSelectImageFromDocumentPicker(
	options: SelectImageFromDocumentPickerOptions
): SelectImageFromDocumentPickerResult {
	const {
		supportedImageExtensions,
		defaultImageLocation,
		onIsBusyChange,
		isMultiple
	} = options;

	const [selectFileDialog, openSelectFileDialog] = useSelectFilesDialog();

	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const openPicker = useCallback(
		async (): Promise<TryResult<FileSelection>> => {
			const { sharePoint } = documentsConfiguration();

			let initialLocation: ItemPickerLocation | null = null;

			if (defaultImageLocation) {
				initialLocation = await convertToItemPickerLocation(
					defaultImageLocation,
					onIsBusyChange,
					globalCancellation
				);
			}

			return openSelectFileDialog({
				initialLocation: initialLocation ?? undefined,
				options: {
					sharePointOptions: {
						isEnabled: sharePoint.isConfigured,
						shouldShowPersonalViews: true,
						availableDestination: { kind: "all" }
					},
					webDavOptions: {
						isEnabled: true,
						availableDestination: {
							kind: "all"
						}
					},
					commonOptions: {
						mode: isMultiple
							? ItemPickerMode.MultipleFiles
							: ItemPickerMode.SingleFileOrOneNoteNotebook,
						fileTypeFilter: {
							kind: FileTypeFilterKind.ExtensionList,
							extensions: supportedImageExtensions
						}
					}
				}
			});
		},
		[
			defaultImageLocation,
			openSelectFileDialog,
			isMultiple,
			supportedImageExtensions,
			onIsBusyChange,
			globalCancellation
		]
	);

	const selectImageHandler = useCallback(
		async (): Promise<TryResult<readonly PendingImageDownload[]>> => {
			const result: TryResult<FileSelection> = await openPicker();

			if (!result.wasSuccessful) {
				return {
					wasSuccessful: false
				};
			}

			const itemDownloaders: readonly PendingImageDownload[] = getPendingImagesDownloads(result.result);

			return {
				wasSuccessful: true,
				result: itemDownloaders
			};
		},
		[openPicker]
	);

	return useMemo(
		(): SelectImageFromDocumentPickerResult => {
			return {
				dialogElement: selectFileDialog,
				onSelectImage: selectImageHandler
			};
		},
		[
			selectFileDialog,
			selectImageHandler
		]
	);
}

function getPendingImagesDownloads(
	fileSelection: FileSelection
): readonly PendingImageDownload[] {
	const { kind, items } = fileSelection;
	const pendingImagesDownloads: PendingImageDownload[] = [];

	switch (kind) {
		case "sharepoint":
			for (const item of items) {
				const cancellation = new AbortController();
				pendingImagesDownloads.push({
					file: downloadImageFromUrl(
						getDownloadFileUrl(item.id, item.location),
						item.name,
						cancellation.signal
					),
					cancel: (): void => cancellation.abort()
				});
			}
			break;
		case "webdav":
			for (const item of items) {
				const cancellation = new AbortController();
				pendingImagesDownloads.push({
					file: downloadImageFromUrl(
						getFileDownloadUrl(item.id, item.serverId),
						item.name,
						cancellation.signal
					),
					cancel: (): void => cancellation.abort()
				});
			}
			break;
		default:
			never(kind);
	}

	return pendingImagesDownloads;
}

async function downloadImageFromUrl(
	url: string,
	fileName: string,
	signal?: AbortSignal
): Promise<File> {
	try {
		const client = new HttpClient();
		const imageBlob: Blob = await client.getBlob(
			url,
			undefined,
			{ signal }
		);

		const imageFile: File = new File(
			[imageBlob],
			fileName,
			{ type: imageBlob.type }
		);

		return imageFile;
	} catch (e) {
		console.error("Could not load the selected image", e);
		throw e;
	}
}
